<script setup lang="ts">

import {computed, onMounted, ref} from "vue";
import {useLoginInfoStore} from "@/stores/loginInfo";
import Translator from "@/utils/common/Translator";
import NotificationPopupItem from "@/components/navBar/notifications/NotificationPopupItem.vue";
import {storeToRefs} from "pinia";
import NoItems from "@/components/navBar/approvals/NoItems.vue";
import NamaIcon from "@/components/icons/NamaIcon.vue";
import AnimatedBell from "@/components/icons/AnimatedBell.vue";
import NotificationUtils from "@/utils/broadcast/NotificationUtils.ts";
import GeneralAnnouncements from "@/components/announcements/GeneralAnnouncements.vue";
import GeneralAnnouncementsUtil from "@/utils/common/GeneralAnnouncementsUtil.ts";
import {useNamaFlagsStore} from "@/stores/nama-flags-store.ts";
import DTOUserNotification = namaTypes.DTOUserNotification;

let {notifications, notificationsCount} = storeToRefs(useLoginInfoStore());
let {barsDarkMode} = storeToRefs(useNamaFlagsStore());
let isPopupVisible = ref(false);

const announcements = GeneralAnnouncementsUtil.announcements;
const hasAnnouncements = computed(() => announcements.value.length > 0);
const badgeCount = computed(() => notificationsCount.value + announcements.value.length);
const bellColor = computed(() => barsDarkMode.value ? 'var(--nama-primary-dark)' : 'var(--nama-primary-light)');

onMounted(async () => await GeneralAnnouncementsUtil.load());

const viewAllNotificationsLink = computed(() => NotificationUtils.viewAllNotificationsLink());

async function markAsRead() {
  await NotificationUtils.markAsReadTop5Notifications();
  isPopupVisible.value = false;
}
</script>

<template>
  <div class="tw-flex tw-justify-center">
    <AnimatedBell :title="Translator.translateId('notifications')" :active="hasAnnouncements" color="primary"
                  class="focus:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-white/75 hover:tw-cursor-pointer">
      <NamaIcon default-icon="nm-notification" class="tw-size-6 compact:tw-size-[22px] ultra:tw-size-5"
                :colored="hasAnnouncements" :default-color="bellColor"/>
      <span v-if="hasAnnouncements"
            class="tw-absolute tw-flex tw-items-center tw-gap-[3px] tw-h-[17px] tw-px-[5px] tw-rounded-[9px]
                   tw-bg-primary-light tw-font-semibold tw-text-sm ultra:tw-text-xs tw-text-white
                   tw--top-1/3 tw-left-1/2 tw-leading-[10px]" v-text="badgeCount > 99 ? '+99' : badgeCount"/>
      <span v-else-if="notificationsCount>0" v-text="notificationsCount > 99 ? '+99' : notificationsCount"
            class="tw-absolute tw-bg-red-600 tw-rounded-[25%] tw-text-sm ultra:tw-text-xs tw-p-1 ultra:tw-p-0.5 tw-text-white tw--top-1/3 tw-left-1/2 tw-leading-[10px]"/>
    </AnimatedBell>
    <q-menu v-if="useLoginInfoStore().isLoggedIn" v-model="isPopupVisible" self="top middle" :offset=[60,15] id="notificationMenu"
            class="tw-max-h-[70vh] tw-w-full desktop:tw-w-[32rem] desktop:tw-max-w-[40vw] tw-flex tw-flex-col dark:tw-bg-namaBackground-deep-dark">
      <div
        class="tw-flex tw-shrink-0 tw-justify-between tw-items-center tw-min-h-[46px] tw-px-4 tw-border-b tw-border-[#eef1f5] dark:tw-border-gray-600"
        v-if="(notifications&&notifications.length>0)||hasAnnouncements">
        <div class="tw-text-sm tw-font-bold dark:tw-text-namaFontColor-input-dark"
             v-text="Translator.translateId(hasAnnouncements ? 'notificationsAndAnnouncements' : 'ShowNotifications')"/>
        <div v-if="notifications&&notifications.length>0"
             class="tw-text-[12.5px] tw-text-primary-light dark:tw-text-primary-dark hover:tw-underline hover:tw-cursor-pointer" @click="markAsRead"
             v-text="Translator.translateId('markAsRead')"/>
      </div>
      <GeneralAnnouncements class="tw-shrink-0"/>
      <div class="tw-overflow-y-auto tw-grow tw-max-h-full">
          <NotificationPopupItem v-for="(notification, index) in notifications" :key="index" :notification="notification.values as DTOUserNotification"/>
          <NoItems v-if="!notifications||notifications.length==0" :message="Translator.translateId('noNewNotifications')"/>
      </div>
      <div class="tw-w-full flex tw-shrink-0">
        <a
          class="tw-w-full tw-bg-[#4a5568] hover:tw-bg-[#3d4757] dark:tw-bg-black tw-rounded-t-none tw-text-[13.5px] tw-p-3 tw-leading-tight tw-rounded-b-[4px] tw-font-semibold tw-text-namaFontColor-input-dark tw-cursor-pointer tw-text-center"
          :href="viewAllNotificationsLink"
          v-text="Translator.translateId('viewAllNotifications')"/>
      </div>
    </q-menu>
  </div>
</template>
